import React, { useState, useEffect } from 'react';
import { ref, set, onValue, update } from 'firebase/database';
import { database } from '../firebaseConfig';
import './AddTopics.css';

const AddTopics = () => {
    const [topics, setTopics] = useState({});
    const [newTopic, setNewTopic] = useState({
        topic: '',
        topicSpecificNote: '',
        type: 'intro',
        subjectLine: ''
    });
    const [editingTopic, setEditingTopic] = useState(null);

    useEffect(() => {
        const topicsRef = ref(database, '/WorldSettings/topicsTemplate');
        const unsubscribe = onValue(topicsRef, (snapshot) => {
            if (snapshot.exists()) {
                setTopics(snapshot.val());
            }
        });

        return () => unsubscribe();
    }, []);

    // Vereinfachte Topic-Typen
    const topicTypes = [
        { value: 'intro', label: 'Introduction Email' },
        { value: 'follow_up_1', label: 'First Follow-Up' },
        { value: 'follow_up_2', label: 'Second Follow-Up' },
        { value: 'follow_up_3', label: 'Third Follow-Up' },
        { value: 'custom_reply', label: 'Custom Reply' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        const topicsRef = ref(database, '/WorldSettings/topicsTemplate');
        
        // Stelle sicher, dass der Topic-Typ einer der erlaubten Typen ist
        if (!['intro', 'follow_up_1', 'follow_up_2', 'follow_up_3', 'custom_reply'].includes(newTopic.type)) {
            newTopic.type = 'custom_reply'; // Standardmäßig auf custom_reply setzen
        }
        
        const topicId = editingTopic || `topic_${Date.now()}`;
        const updatedTopics = {
            ...topics,
            [topicId]: {
                topic: newTopic.topic,
                topicSpecificNote: newTopic.topicSpecificNote,
                type: newTopic.type,
                subjectLine: newTopic.subjectLine
            }
        };

        await set(topicsRef, updatedTopics);

        setNewTopic({
            topic: '',
            topicSpecificNote: '',
            type: 'intro',
            subjectLine: ''
        });
        setEditingTopic(null);
    };

    const handleEdit = (topicId, topicData) => {
        setEditingTopic(topicId);
        setNewTopic({
            topic: topicData.topic,
            topicSpecificNote: topicData.topicSpecificNote,
            type: topicData.type || 'intro',
            subjectLine: topicData.subjectLine || ''
        });
    };

    const handleDelete = async (topicKey) => {
        if (window.confirm('Sind Sie sicher, dass Sie dieses Topic löschen möchten?')) {
            const updatedTopics = { ...topics };
            delete updatedTopics[topicKey];
            
            const topicsRef = ref(database, '/WorldSettings/topicsTemplate');
            await set(topicsRef, updatedTopics);
        }
    };

    return (
        <div className="add-topics-container">
            <h2>{editingTopic ? 'Topic bearbeiten' : 'Neues Topic hinzufügen'}</h2>
            
            <form onSubmit={handleSubmit} className="add-topics-form">
                <div className="form-group">
                    <label>Topic Type:</label>
                    <select
                        value={newTopic.type}
                        onChange={(e) => setNewTopic({...newTopic, type: e.target.value})}
                        className="topic-type-select"
                    >
                        {topicTypes.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Topic:</label>
                    <input
                        type="text"
                        value={newTopic.topic}
                        onChange={(e) => setNewTopic({...newTopic, topic: e.target.value})}
                        placeholder="Name des Topics eingeben"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Default Subject Line (Optional):</label>
                    <input
                        type="text"
                        value={newTopic.subjectLine}
                        onChange={(e) => setNewTopic({...newTopic, subjectLine: e.target.value})}
                        placeholder="z.B. BonusClub - Boost Your Online Visibility"
                        className="subject-line-input"
                    />
                </div>

                <div className="form-group">
                    <label>Topic Specific Note:</label>
                    <textarea
                        value={newTopic.topicSpecificNote}
                        onChange={(e) => setNewTopic({...newTopic, topicSpecificNote: e.target.value})}
                        placeholder="Anweisungen für die KI eingeben..."
                        required
                        rows={12}
                    />
                </div>

                <button type="submit" className="submit-button">
                    {editingTopic ? 'Topic aktualisieren' : 'Topic hinzufügen'}
                </button>
            </form>

            <div className="topics-list">
                <h3>Vorhandene Topics</h3>
                {Object.entries(topics).map(([topicId, topicData]) => (
                    <div key={topicId} className="topic-item">
                        <div className="topic-header">
                            <h4>{topicData.topic}</h4>
                            <span className="topic-type-badge">{
                                topicTypes.find(t => t.value === topicData.type)?.label || 'Introduction Email'
                            }</span>
                        </div>
                        <p className="topic-id">ID: {topicId}</p>
                        {topicData.subjectLine && (
                            <p className="subject-line">
                                <strong>Subject Line:</strong> {topicData.subjectLine}
                            </p>
                        )}
                        <p className="prompt-preview">
                            <strong>Topic Specific Note:</strong> {topicData.topicSpecificNote}
                        </p>
                        <div className="topic-actions">
                            <button onClick={() => handleEdit(topicId, topicData)}>Bearbeiten</button>
                            <button onClick={() => handleDelete(topicId)} className="delete-button">
                                Löschen
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AddTopics; 